import React from "react";
import { createPortal } from "react-dom";
import SvgIcon from "./SvgIcon";
import useDropdownOverlay, {
  getOverlayStyle,
} from "../hooks/useDropdownOverlay";

const EDIT_PENCIL_ICON = "/img/new-icons/edit-pencil.svg";

/**
 * Same shape/positioning as parts/dropdown.js (built on the same
 * useDropdownOverlay hook — see that file for the shared open/close +
 * fixed-position overlay logic), but each row is a two-target control
 * instead of one: clicking the label selects the option (same as Dropdown),
 * clicking the trailing edit icon calls `option.onEdit(option)` instead —
 * for cases like Nutrition Table's template switcher, where a saved
 * template needs to be both selectable AND directly editable from the same
 * list, without leaving the dropdown or hunting for a second control.
 *
 * options: [{ text, value, onEdit? }] — onEdit omitted → no edit icon on
 * that row (e.g. the "Default View" option, which has nothing to edit).
 *
 * The edit button's onMouseDown stops propagation so it doesn't also
 * trigger the row's own onMouseDown (select) — both are mousedown (not
 * click) so a select vs. blur race doesn't close the menu before the
 * handler runs, same reasoning as Dropdown's handleSelect.
 */
const DropdownActions = ({
  summaryText,
  options,
  onSelectFn,
  fullOption,
  alignRight,
  disabled,
  overlay = false,
  openUpward = false,
  summaryStyles,
  optionStyles,
}) => {
  const { opened, toggle, close, summaryRef, listRef, listPosition } = useDropdownOverlay({
    overlay,
    alignRight: Boolean(alignRight),
    openUpward,
    remeasureKey: options,
  });

  const handleSelect = (option) => {
    onSelectFn(fullOption ? option : option.value);
    close();
  };

  const handleEdit = (e, option) => {
    e.stopPropagation();
    option.onEdit(option);
    close();
  };

  const listItems = options?.map((option) => (
    <li key={option.value} style={{ ...optionStyles }}>
      <div
        className="dropdown-actions__row"
        onMouseDown={() => handleSelect(option)}
      >
        <span className="dropdown-actions__label">{option.text || option.label}</span>
        {option.onEdit && (
          <button
            type="button"
            className="dropdown-actions__edit-btn"
            onMouseDown={(e) => handleEdit(e, option)}
            aria-label={option.editLabel}
          >
            <SvgIcon
              src={EDIT_PENCIL_ICON}
              noFilterOnLight
              classNames="dropdown-actions__edit-icon"
            />
          </button>
        )}
      </div>
    </li>
  ));

  const inlineList = (
    <ul role="listbox" className={opened ? "opened" : ""}>
      {listItems}
    </ul>
  );

  const overlayList =
    overlay && opened && listPosition
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="dropdown-overlay-list opened"
            style={getOverlayStyle(listPosition)}
          >
            {listItems}
          </ul>,
          document.body
        )
      : null;

  return (
    <details
      role="list"
      className={disabled ? "disabled" : ""}
      open
      data-align-right={alignRight ? true : undefined}
      data-overlay={overlay ? true : undefined}
    >
      <summary
        ref={summaryRef}
        onClick={(e) => {
          if (disabled || !options || options.length < 1) {
            e.preventDefault();
            return;
          }
          toggle(e);
        }}
        onBlur={(e) => {
          if (!overlay) {
            e.preventDefault();
            close();
          }
        }}
        aria-haspopup="listbox"
        style={{ ...summaryStyles }}
      >
        <p>{summaryText}</p>
        <SvgIcon
          src={opened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
        />
      </summary>
      {overlay ? overlayList : inlineList}
    </details>
  );
};

export default DropdownActions;
